import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import NewsletterAdmin from "@/components/NewsletterAdmin";
import BillingPanel from "@/components/BillingPanel";
import PasswordSetupBanner from "@/components/PasswordSetupBanner";

export default function Dashboard() {
  const { user, loading: authLoading, setSession } = useAuth();
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const subSession = params.get("sub_session");
  // Initialize claiming=true synchronously if we have a sub_session and no user yet —
  // this prevents the auth-gate Navigate from firing before the claim effect runs
  const [claiming, setClaiming] = useState(() => Boolean(subSession));
  const [claimError, setClaimError] = useState(null);

  // Anonymous post-checkout claim — runs BEFORE auth gate
  useEffect(() => {
    if (!subSession || authLoading || user) {
      // If we eagerly set claiming=true but turns out we already have a user or no session, clear it
      if (claiming && (!subSession || user)) setClaiming(false);
      return;
    }
    setClaiming(true);
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.post(`/billing/claim/${subSession}`);
        if (cancelled) return;
        setSession(data.token, data.user);
        toast.success(
          data.is_new
            ? "Welcome to the forge — account created. You can set a password from your dashboard."
            : "Welcome back — subscription synced."
        );
        const next = new URLSearchParams(params);
        next.delete("sub_session");
        if (data.user?.needs_password_setup) next.set("setup_password", "1");
        setParams(next, { replace: true });
        setClaiming(false);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 402 && attempts < 10) {
          // Payment not yet recorded — Stripe still processing, retry
          setTimeout(poll, 1500);
          return;
        }
        if (!cancelled) {
          const rawDetail = err?.response?.data?.detail;
          const friendly =
            typeof rawDetail === "string" && rawDetail.length < 200 && !rawDetail.includes("<")
              ? rawDetail
              : "Could not confirm your subscription. Try logging in to view your membership.";
          console.error("Claim error:", rawDetail || err);
          setClaimError(friendly);
          setClaiming(false);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [subSession, authLoading]);

  // Authed sync after checkout
  useEffect(() => {
    if (!user || !subSession) return;
    api.post(`/billing/sync/${subSession}`)
      .then(() => {
        const next = new URLSearchParams(params);
        next.delete("sub_session");
        setParams(next, { replace: true });
      })
      .catch(() => {});
  }, [user, subSession]);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/plans/mine"), api.get("/orders/mine")])
      .then(([p, o]) => {
        setPlans(p.data);
        setOrders(o.data);
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return null;

  if (claiming) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="dashboard-claiming">
        <Loader2 className="w-10 h-10 animate-spin text-[#FF3333] mx-auto" />
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333] mt-6">UNLOCKING THE FORGE</div>
        <h1 className="font-heading uppercase text-4xl mt-3">Confirming your membership...</h1>
        <p className="font-sub text-white/60 mt-3">
          Stripe is finalizing your payment. This usually takes a few seconds.
        </p>
      </div>
    );
  }

  if (claimError && !user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="dashboard-claim-error">
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">CLAIM FAILED</div>
        <h1 className="font-heading uppercase text-3xl mt-3">{claimError}</h1>
        <Link to="/auth" className="btn-forged-red mt-6 inline-flex">Log in to your account</Link>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="dashboard-page">
      <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF]">ATHLETE FILE</div>
      <h1 className="font-heading uppercase text-5xl md:text-7xl leading-[0.9] mt-2" data-testid="dashboard-greeting">
        Welcome, <span className="text-stroke-red grunge-text">{user.name}</span>
      </h1>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF3333]" /></div>
      ) : (
        <>
          <div className="mt-10">
            <PasswordSetupBanner />
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mt-2">
            <BillingPanel />
            <NewsletterAdmin />
          </div>
          <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading uppercase text-2xl">My Plans</h2>
              <Link to="/questionnaire" className="font-mono text-[10px] tracking-widest text-[#00E5FF] hover:text-white">+ NEW PLAN</Link>
            </div>
            {plans.length === 0 ? (
              <div className="brutal-border bg-[#0a0a0a] p-8 text-center text-white/40 font-sub">
                No plans yet. <Link to="/questionnaire" className="text-[#FF3333] hover:underline">Generate your first</Link>.
              </div>
            ) : (
              <div className="space-y-3">
                {plans.map((p) => (
                  <Link key={p.id} to={`/plan/${p.id}`} data-testid={`dashboard-plan-${p.id}`} className="block brutal-border bg-[#0a0a0a] p-5 hover:border-[#FF3333]/40 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-heading uppercase tracking-wide flex items-center gap-2">
                          {p.plan?.mantra || p.preview?.mantra || "Forged Protocol"}
                          {!p.unlocked && (
                            <span className="font-mono text-[9px] tracking-widest text-[#FF3333] border border-[#FF3333]/40 px-1.5 py-0.5">LOCKED</span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-white/40 mt-1">
                          {new Date(p.created_at).toLocaleDateString()} · {p.questionnaire?.goal} · {p.questionnaire?.days_per_week}d/wk
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/40" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-heading uppercase text-2xl mb-4">Orders</h2>
            {orders.length === 0 ? (
              <div className="brutal-border bg-[#0a0a0a] p-8 text-center text-white/40 font-sub">
                No orders yet.
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`dashboard-order-${o.id}`} className="brutal-border bg-[#0a0a0a] p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono text-[10px] text-white/40 tracking-widest">
                          {new Date(o.created_at).toLocaleDateString()}
                        </div>
                        <div className="font-sub text-white mt-1">
                          {(o.items || []).map((it, i) => (
                            <span key={`${it.product_id}-${i}`} className="mr-3">{it.product_id || "Item"} ×{it.quantity}</span>
                          ))}
                        </div>
                      </div>
                      <div className="font-heading text-2xl text-[#00E5FF]">${o.amount?.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </>
      )}
    </div>
  );
}
